import { Box, Stack, Typography } from '@mui/material'
import { com, Nullable } from '@eidu/web'
import { fieldValueToString } from '../../../../../domain/entity/DraftFieldValue'
import { EntitySnapshotDiff, FieldChange, LinkChange } from './entitySnapshotDiff'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue

type EntityDiffSummaryProps = {
  diff: EntitySnapshotDiff
  entityType?: EntityType
  beforeLabel?: string
  afterLabel?: string
}

const formatField = (value: Nullable<FieldValue> | undefined): string => {
  if (value === undefined) return '∅'
  return fieldValueToString(value) || '""'
}

const formatLink = (value: string | undefined): string => value ?? '∅'

const fieldName = (fieldId: FieldId, entityType?: EntityType): string => {
  const idString = fieldId.toString()
  if (!entityType) return idString
  const fieldDef = entityType.fields.asJsReadonlyArrayView().find((f) => f.id.toString() === idString)
  return fieldDef?.name ?? idString
}

const ChangeRow = ({ label, before, after }: { label: string; before: string; after: string }) => (
  <Stack
    direction="row"
    sx={{
      alignItems: 'stretch',
      borderTop: 1,
      borderColor: 'divider',
      '&:first-of-type': { borderTop: 0 },
    }}
  >
    <Box sx={{ flex: '0 0 28%', px: 1.5, py: 1, borderRight: 1, borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
    <Box
      sx={{
        flex: 1,
        px: 1.5,
        py: 1,
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'error.dark' : '#fff1f0'),
        color: 'text.primary',
        fontFamily: 'monospace',
        fontSize: 13,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
      }}
    >
      {before}
    </Box>
    <Box
      sx={{
        flex: 1,
        px: 1.5,
        py: 1,
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'success.dark' : '#e6ffec'),
        color: 'text.primary',
        fontFamily: 'monospace',
        fontSize: 13,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
      }}
    >
      {after}
    </Box>
  </Stack>
)

const HeaderRow = ({ beforeLabel, afterLabel }: { beforeLabel: string; afterLabel: string }) => (
  <Stack direction="row" sx={{ bgcolor: 'action.hover', alignItems: 'stretch' }}>
    <Box sx={{ flex: '0 0 28%', px: 1.5, py: 0.75, borderRight: 1, borderColor: 'divider' }}>
      <Typography variant="overline" color="text.secondary">
        Field / Link
      </Typography>
    </Box>
    <Box sx={{ flex: 1, px: 1.5, py: 0.75, borderRight: 1, borderColor: 'divider' }}>
      <Typography variant="overline" color="text.secondary">
        {beforeLabel}
      </Typography>
    </Box>
    <Box sx={{ flex: 1, px: 1.5, py: 0.75 }}>
      <Typography variant="overline" color="text.secondary">
        {afterLabel}
      </Typography>
    </Box>
  </Stack>
)

const fieldRow = (change: FieldChange, entityType?: EntityType) => (
  <ChangeRow
    key={`field-${change.fieldId.toString()}`}
    label={`Field · ${fieldName(change.fieldId, entityType)}`}
    before={formatField(change.before)}
    after={formatField(change.after)}
  />
)

const linkRow = (change: LinkChange) => (
  <ChangeRow
    key={`link-${change.key.key}`}
    label={`Link · ${change.key.key}`}
    before={formatLink(change.before)}
    after={formatLink(change.after)}
  />
)

const EntityDiffSummary = ({
  diff,
  entityType,
  beforeLabel = 'Before',
  afterLabel = 'After',
}: EntityDiffSummaryProps) => (
  <Box
    sx={{
      mt: 1.5,
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
    }}
  >
    <HeaderRow beforeLabel={beforeLabel} afterLabel={afterLabel} />
    {diff.equal ? (
      <Box sx={{ px: 1.5, py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          No field or link changes.
        </Typography>
      </Box>
    ) : (
      <>
        {diff.fieldChanges.map((c) => fieldRow(c, entityType))}
        {diff.linkChanges.map((c) => linkRow(c))}
      </>
    )}
  </Box>
)

export default EntityDiffSummary
